import React, { useState } from 'react';
import { api } from '../api.js';
import { Card, ErrorBanner, ImageField, Loading, Modal, Money, ProofReference, StateChip, SummaryList, useAsync, useSession, useToast } from '../ui.jsx';
import { centsToInput, dateLabel, monthLabel, money, nameOf, parseAmountToCents, recipientLabel } from '../lib/format.js';
import { Link } from '../main.jsx';

/** Unit-first member home with a cross-unit attention area. */
export default function MemberHome() {
  const session = useSession();
  const [unitId, setUnitId] = useState(session.units[0] ? session.units[0].id : null);
  const [submitting, setSubmitting] = useState(null);
  const home = useAsync(() => api.memberHome(), []);

  if (home.loading) return <Loading />;
  if (home.error) return <ErrorBanner error={home.error} />;

  const unitBucket = home.data.units.find((bucket) => bucket.unit.id === unitId) || home.data.units[0];

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <div className="eyebrow">合租帳務 · {home.data.today}</div>
          <h1>你好，{session.member.display_name}</h1>
          <p className="small muted">先選單位，逐個地方處理租金、雜費、裝修與設備；跨單位待處理事項在下方一併列出。</p>
        </div>
      </div>

      <Card
        title="需要你處理"
        subtitle="跨單位 · 逾期只作額外標記，不取代付款項目狀態"
        testId="attention-area"
        actions={<div className="metric"><span>尚待付款</span><strong>{money(home.data.attention_total_cents)}</strong></div>}
      >
        {home.data.attention.length === 0 ? (
          <p className="small muted" data-testid="attention-empty">目前沒有待處理的應付款。</p>
        ) : (
          <div>
            {home.data.attention.map((entry) => (
              <div className="item-row" key={entry.due.due_id} data-testid={`attention-${entry.item_id}`}>
                <div>
                  <div className="title">
                    <Link to={`/items/${entry.item_id}`}>{entry.title}</Link>
                  </div>
                  <div className="meta">
                    {session.units.find((unit) => unit.id === entry.unit_id)?.name} · {entry.category} · 收款人 {recipientLabel(session.members, entry)} · 到期 {dateLabel(entry.due_date)}
                  </div>
                </div>
                <div className="right">
                  <Money cents={entry.due.remaining_cents} testId={`attention-remaining-${entry.item_id}`} />
                  <StateChip state={entry.due.state} label={entry.due.state_label} overdue={entry.due.overdue} />
                  {entry.status === 'issued' && entry.due.remaining_cents > 0 ? (
                    <button type="button" className="primary small" onClick={() => setSubmitting(entry)} data-testid={`submit-proof-${entry.item_id}`}>
                      提交付款證明
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="unit-tabs" role="group" aria-label="選擇單位">
        {home.data.units.map((bucket) => (
          <button
            type="button"
            key={bucket.unit.id}
            aria-pressed={bucket.unit.id === unitBucket.unit.id}
            onClick={() => setUnitId(bucket.unit.id)}
            data-testid={`unit-tab-${bucket.unit.name}`}
          >
            {bucket.unit.name}
          </button>
        ))}
      </div>

      <Card title={`${unitBucket.unit.name} · 我的應付款`} testId="unit-panel">
        <div className="grid three" style={{ marginBottom: 14 }}>
          <div className="metric"><span>我的應付款</span><strong>{money(unitBucket.totals.amount_cents)}</strong></div>
          <div className="metric"><span>已確認付款</span><strong data-testid="unit-confirmed">{money(unitBucket.totals.confirmed_cents)}</strong></div>
          <div className="metric"><span>仍待付款</span><strong data-testid="unit-remaining">{money(unitBucket.totals.remaining_cents)}</strong></div>
          <div className="metric"><span>待確認</span><strong>{money(unitBucket.totals.pending_cents)}</strong></div>
          <div className="metric"><span>待處理結餘</span><strong data-testid="unit-balance">{money(unitBucket.totals.pending_balance_cents)}</strong></div>
        </div>
        {unitBucket.items.length === 0 ? (
          <p className="small muted">這個單位暫時沒有你的應付款。</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>付款項目</th>
                  <th>分類</th>
                  <th>帳務月份</th>
                  <th>到期日</th>
                  <th className="num">我的應付款</th>
                  <th className="num">仍待付款</th>
                  <th>狀態</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {unitBucket.items.map((entry) => (
                  <tr key={entry.due.due_id} data-testid={`unit-item-${entry.item_id}`}>
                    <td>
                      <Link to={`/items/${entry.item_id}`}>{entry.title}</Link>
                      {entry.historical ? <div className="small muted">歷史補錄</div> : null}
                    </td>
                    <td>{entry.category}</td>
                    <td>{monthLabel(entry.accounting_month)}</td>
                    <td>{dateLabel(entry.due_date)}</td>
                    <td className="num">{money(entry.due.amount_cents)}</td>
                    <td className="num" data-testid={`row-remaining-${entry.item_id}`}>{money(entry.due.remaining_cents)}</td>
                    <td><StateChip state={entry.due.state} label={entry.due.state_label} overdue={entry.due.overdue} /></td>
                    <td>
                      {entry.status === 'issued' && !entry.historical && entry.due.remaining_cents > 0 ? (
                        <button type="button" className="small" onClick={() => setSubmitting(entry)} data-testid={`row-submit-${entry.item_id}`}>交證明</button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {submitting ? (
        <SubmitProofModal
          entry={submitting}
          onClose={() => setSubmitting(null)}
          onDone={() => { setSubmitting(null); home.reload(); }}
        />
      ) : null}
    </div>
  );
}

export function SubmitProofModal({ entry, onClose, onDone }) {
  const session = useSession();
  const notify = useToast();
  const [amount, setAmount] = useState(centsToInput(entry.due.remaining_cents));
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setError(null);
    const cents = parseAmountToCents(amount);
    if (cents === null || cents <= 0) {
      setError({ message: '付款金額必須是大於 0 的港幣金額（最多兩位小數）' });
      return;
    }
    if (!file) {
      setError({ message: '每次付款提交必須附上一張付款證明' });
      return;
    }
    setBusy(true);
    try {
      const formData = new FormData();
      formData.set('due_id', entry.due.due_id);
      formData.set('amount_cents', String(cents));
      formData.set('image', file);
      const result = await api.submitPayment(formData);
      notify(`付款證明已提交（${result.submission.reference}），等待確認人處理`);
      onDone();
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="提交付款證明" subtitle={`${entry.title} · 付款給 ${recipientLabel(session.members, entry)}`} onClose={onClose}>
      <SummaryList rows={[
        ['我的應付款', money(entry.due.amount_cents)],
        ['已確認付款', money(entry.due.confirmed_cents)],
        ['仍待付款', money(entry.due.remaining_cents)],
        ['確認人', nameOf(session.members, entry.confirmer_member_id)],
      ]} />
      <div className="banner info small" style={{ margin: '12px 0' }}>
        付款證明只代表你申報已付款，並不代表銀行已核實交易。確認人確認後才會扣減仍欠金額。
      </div>
      <ErrorBanner error={error} />
      <div className="stack">
        <label className="field">
          <span>今次付款金額（HKD）</span>
          <input value={amount} onChange={(event) => setAmount(event.target.value)} inputMode="decimal" data-testid="submission-amount" />
        </label>
        <ImageField file={file} onChange={setFile} />
        {entry.due.submissions.length ? (
          <div className="small muted">
            已有 {entry.due.submissions.length} 次提交：
            {entry.due.submissions.map((submission) => (
              <span key={submission.id}> <ProofReference reference={submission.reference} /> {submission.state_label}（{money(submission.amount_cents)}）</span>
            ))}
          </div>
        ) : null}
      </div>
      <div className="modal-actions">
        <button type="button" onClick={onClose}>取消</button>
        <button type="button" className="primary" disabled={busy} onClick={submit} data-testid="submission-confirm">送出付款證明</button>
      </div>
    </Modal>
  );
}
